import xhr from 'xhr';
import uuid from 'uuid/v4';

import config from 'config';

const schedulingModeError = () => new Error('Invalid queue scheduling mode');

function getWaitTimeExp(retryCount) {
  return Math.pow(2, retryCount) * 1000;
}

export class LogsQueue {
  constructor(
    initial = [],
    {autoProcess = true, onUpdate, onUnloggableError} = {}
  ) {
    this.currentTask = null;
    this.pendingTasks = Array.isArray(initial) ? initial : [];
    this.retryQueue = {};
    this.networkRetries = 0;
    this.options = {
      autoProcess,
      onUpdate,
      onUnloggableError
    };
  }

  serialize() {
    let tasks = Object.values(this.retryQueue);
    let pending = this.pendingTasks.concat();
    if (this.currentTask) {
      pending = pending.unshift(this.currentTask);
    }

    return tasks.concat(pending);
  }

  onUpdate() {
    if (this.options.onUpdate) {
      this.options.onUpdate(this.serialize());
    }
  }

  _schedule(mode = 'push', request) {
    if (mode === 'push') {
      this.pendingTasks.push(request);
    } else if (mode === 'unshift') {
      this.pendingTasks.unshift(request);
    } else {
      throw schedulingModeError();
    }

    this.onUpdate();

    this.process();
  }

  push(request) {
    this._schedule('push', request);
  }

  unshift(request) {
    this._schedule('unshift', request);
  }

  process() {
    if (this.currentTask) {
      return;
    }

    if (this.pendingTasks.length === 0) {
      return;
    }

    this.currentTask = this.pendingTasks.shift();
    this._makeCall();
  }

  retry() {
    this._makeCall();
  }

  _makeCall() {
    if (!this.currentTask) {
      return;
    }

    const method = 'POST';
    const url = `${config.api}/logs`;

    // Remove the request from the retry queue
    if (this.currentTask.id && this.retryQueue[this.currentTask.id]) {
      delete this.retryQueue[this.currentTask.id];
    }

    if (typeof this.currentTask !== 'object') {
      console.warn('Invalid log data', this.currentTask);
      this.currentTask = null;
      this.process();
      return;
    }

    const data = {data: JSON.stringify(this.currentTask)};

    // If a sync request is made and sendBeacon API is supported, use that
    if (
      this.currentTask.sync &&
      config.useBeacon &&
      navigator &&
      navigator.sendBeacon
    ) {
      this._sendBeacon({url, data});
      this.currentTask = null;
      this.process();
      return;
    }

    const queue = this;

    xhr(
      {
        method,
        url,
        body: data,
        json: true,
        sync: Boolean(this.currentTask.sync),
        timeout: config.requestsQueueTimeout
      },
      (err, resp /*, body*/) => {
        if (err) {
          if (navigator && !navigator.onLine) {
            // Offline
            this._handleNetworkError();
            return;
          }

          if (err.code === 'ETIMEDOUT') {
            // Timeout
            this._handleNetworkError();
            return;
          }

          this._handleProbablyPermanentError();
          return;
        }

        const {statusCode} = resp;

        if (statusCode >= 400 && statusCode < 500) {
          this._handleProbablyPermanentError();
          return;
        }

        if (statusCode >= 500) {
          this._handleProbablyTemporaryError();
          return;
        }

        if (statusCode >= 200 && statusCode < 300) {
          // The request is successful

          queue.currentTask = null;
          queue.networkRetries = 0;
          this.onUpdate();
          if (queue.options.autoProcess) {
            queue.process();
          }
          return;
        }

        this._handleProbablyPermanentError();
      }
    );
  }

  _handleNetworkError() {
    if (!this.currentTask || this.currentTask.sync) {
      return;
    }

    this.networkRetries += 1;
    let waitTime = Math.min(
      getWaitTimeExp(this.networkRetries - 1),
      config.requestsQueueNetworkMaxWaitInterval
    );
    setTimeout(() => {
      this.retry();
    }, waitTime);
  }

  _handleResponseError(type) {
    if (type !== 'permanent' && type !== 'temporary') {
      throw new Error('Invalid queue error type');
    }

    if (!this.currentTask || this.currentTask.sync) {
      return;
    }

    this.networkRetries = 0;

    const request = Object.assign({}, this.currentTask);
    let expectedRetryType = 'linear';
    if (type === 'temporary') {
      expectedRetryType = 'exponential';
    }
    if (request.retry && request.retry.type === expectedRetryType) {
      request.retry.attempt = Number.isInteger(request.retry.attempt)
        ? request.retry.attempt + 1
        : 1;
    } else {
      request.retry = {
        type: expectedRetryType,
        attempt: 1
      };
    }

    if (request.retry.attempt > config.requestsQueueMaxRetries) {
      if (this.options.onUnloggableError) {
        this.options.onUnloggableError(request);
      }

      // Discard the request
      return;
    }

    // Track the request in the retry queue
    // and assign it a unique id if it does not have it already
    request.id = request.id || uuid();
    if (!this.retryQueue[request.id]) {
      this.retryQueue[request.id] = request;
    }

    let waitTime = config.requestQueuePermanentWaitInterval;
    if (type === 'temporary') {
      waitTime = Math.min(
        getWaitTimeExp(request.retry.attempt - 1),
        config.requestsQueueTemporaryErrorMaxWaitInterval
      );
    }
    setTimeout(() => {
      this.unshift(request);
    }, waitTime);

    this.currentTask = null;
    this.onUpdate();
    if (this.options.autoProcess) {
      this.process();
    }
  }

  _handleProbablyPermanentError() {
    this._handleResponseError('permanent');
  }

  _handleProbablyTemporaryError() {
    this._handleResponseError('temporary');
  }

  _sendBeacon({url, data = {}}) {
    if (!url || !data) {
      return;
    }

    const fd = new FormData();
    Object.keys(data).forEach(key => {
      fd.append(key, data[key]);
    });

    navigator.sendBeacon(url, fd);
  }

  exhaust() {
    if (this.currentTask) {
      // Reschedules the current task (it may be executed twice)
      this.pendingTasks.unshift(this.currentTask);
      this.currentTask = null;
    }

    // Mark all pending tasks with the special sync attribute
    // Requests will be executed in sync mode
    // and errors will be ignored.
    // This mechanism is just a best effort to execute the pending requests.
    // No request should require its successfull execution
    this.pendingTasks.map(task => (task['sync'] = true));

    this.process();
  }
}
